import { useCallback, useRef, useState } from "react";
import UploadNoticeModal from "./UploadNoticeModal";

// The upload notice is a per-browser preference ("don't remind me again"), not session state.
const ACK_KEY = "tg.assistant.attachmentNoticeAck";

function acknowledged() {
  try {
    return window.localStorage.getItem(ACK_KEY) === "1";
  } catch {
    return false; // storage blocked: just keep showing the notice
  }
}

function remember() {
  try {
    window.localStorage.setItem(ACK_KEY, "1");
  } catch {
    /* the notice simply keeps appearing */
  }
}

// Every way of starting an upload (the file picker, paste, drop) goes through request(): the notice
// appears at that moment unless the user chose not to be reminded, and only then does the upload start.
// request([]) means "open the file picker once the notice is accepted".
export default function useUploadGate({ capability, language, upload }) {
  const input = useRef(null);
  const [gate, setGate] = useState(null);

  const proceed = useCallback(
    (files) => {
      if (files.length > 0) upload(files);
      else input.current?.click();
    },
    [upload],
  );

  const request = useCallback(
    (files) => {
      if (acknowledged()) proceed(files);
      else setGate({ mode: "upload", files });
    },
    [proceed],
  );

  const openInfo = useCallback(() => setGate({ mode: "info", files: [] }), []);
  const close = useCallback(() => setGate(null), []);

  const element = capability?.enabled ? (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label="選擇附件"
        accept={capability.acceptedMimeTypes.join(",")}
        onChange={(event) => {
          const files = Array.from(event.target.files);
          event.target.value = "";
          if (files.length > 0) upload(files);
        }}
      />
      {gate && (
        <UploadNoticeModal
          capability={capability}
          language={language}
          files={gate.files}
          mode={gate.mode}
          onClose={close}
          onConfirm={(dontRemind) => {
            if (dontRemind) remember();
            setGate(null);
            proceed(gate.files);
          }}
        />
      )}
    </>
  ) : null;

  return { request, openInfo, element };
}
